/**
 * KYC Detail Modal — shows a KYC submission's full details inside a responsive
 * modal (instead of navigating to /kyc/:id). Mirrors the KycDetail page logic
 * but with an in-modal reject-reason field and an image lightbox.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import ResponsiveModal from '../components/common/ResponsiveModal';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import kycService from '../services/kyc.service';

import './KycDetail.css';

const statusLabels: Record<string, string> = {
  pending: 'در حال بررسی',
  approved: 'تایید شده',
  rejected: 'رد شده',
};

interface KycDetailModalProps {
  open: boolean;
  id: string | null;
  onClose: () => void;
  /** Called after a successful approve/reject so the list can refresh. */
  onChanged?: () => void;
}

const KycDetailModal: React.FC<KycDetailModalProps> = ({ open, id, onClose, onChanged }) => {
  const [row, setRow] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await kycService.getById(id);
      if (res?.status === 'success' && res.data) {
        setRow(res.data);
      } else if (res && ((res as any)._id || (res as any).userId)) {
        setRow(res);
      } else {
        setError((res as any)?.message || 'خطا در دریافت جزئیات');
      }
    } catch (e: any) {
      setError(e?.message || 'خطا در دریافت جزئیات');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // (Re)load whenever the modal opens for a given id; reset transient UI state.
  useEffect(() => {
    if (open && id) {
      setRow(null);
      setShowReject(false);
      setReason('');
      setPreviewSrc(null);
      setZoom(1);
      load();
    }
  }, [open, id, load]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const approve = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await kycService.approve(id);
      onChanged?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'خطا در تایید');
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!id || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await kycService.reject(id, reason.trim());
      onChanged?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'خطا در رد');
    } finally {
      setBusy(false);
    }
  };

  const handleZoomIn = () => setZoom((z) => Math.min(4, z + 0.5));
  const handleZoomOut = () => setZoom((z) => Math.max(0.5, z - 0.5));
  const closePreview = () => { setPreviewSrc(null); setZoom(1); };

  const pending = row?.status === 'pending';

  const renderImage = (label: string, src?: string) => (
    <div className="kyc-image-card">
      <div className="kyc-image-label">{label}</div>
      <div className="kyc-image-container" onClick={() => src && setPreviewSrc(src)}>
        {src ? (
          <img src={src} alt={label} />
        ) : (
          <div className="kyc-image-placeholder">
            <NoPhotographyIcon />
            <p>تصویری آپلود نشده</p>
          </div>
        )}
      </div>
    </div>
  );

  const actions = row ? (
    <>
      <button className="kyc-md-btn ghost" onClick={onClose} disabled={busy}>بستن</button>
      {pending && !showReject && (
        <button className="kyc-md-btn danger-outline" onClick={() => setShowReject(true)} disabled={busy}>
          <CancelIcon fontSize="small" /> رد درخواست
        </button>
      )}
      {pending && showReject && (
        <button className="kyc-md-btn danger" onClick={doReject} disabled={busy || !reason.trim()}>
          {busy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CancelIcon fontSize="small" />}
          ثبت رد
        </button>
      )}
      {pending && (
        <button className="kyc-md-btn approve" onClick={approve} disabled={busy}>
          {busy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CheckCircleIcon fontSize="small" />}
          تایید احراز هویت
        </button>
      )}
    </>
  ) : null;

  return (
    <>
      <ResponsiveModal
        open={open}
        onClose={onClose}
        title="جزئیات احراز هویت"
        maxWidth="md"
        loading={loading}
        error={error}
        disableClose={busy}
        actions={actions}
      >
        {row && (
          <div className="kyc-detail-modal">
            {/* Inline reject reason */}
            {pending && showReject && (
              <div className="kyc-reject-panel">
                <div className="kyc-reject-title">
                  <CancelIcon fontSize="small" /> دلیل رد درخواست
                </div>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="دلیل رد را با جزئیات وارد کنید..."
                  autoFocus
                />
              </div>
            )}

            {/* Info */}
            <div className="kyc-info-card">
              <div className="kyc-info-grid">
                <div className="kyc-info-item">
                  <span className="kyc-info-label">نام کاربر</span>
                  <span className="kyc-info-value">{row.user?.name || row.name || '-'}</span>
                </div>
                <div className="kyc-info-item">
                  <span className="kyc-info-label">ایمیل</span>
                  <span className="kyc-info-value">{row.user?.email || row.email || '-'}</span>
                </div>
                <div className="kyc-info-item">
                  <span className="kyc-info-label">نام (KYC)</span>
                  <span className="kyc-info-value">{row.firstName || '-'}</span>
                </div>
                <div className="kyc-info-item">
                  <span className="kyc-info-label">نام خانوادگی (KYC)</span>
                  <span className="kyc-info-value">{row.lastName || '-'}</span>
                </div>
                <div className="kyc-info-item">
                  <span className="kyc-info-label">شماره ملی</span>
                  <span className="kyc-info-value">{row.nationalIdNumber || '-'}</span>
                </div>
                <div className="kyc-info-item">
                  <span className="kyc-info-label">وضعیت</span>
                  <span className={`status-badge ${row.status}`}>
                    {statusLabels[row.status] || row.status}
                  </span>
                </div>
                <div className="kyc-info-item">
                  <span className="kyc-info-label">تاریخ ارسال</span>
                  <span className="kyc-info-value">{formatDate(row.submittedAt)}</span>
                </div>
                {row.reviewedAt && (
                  <div className="kyc-info-item">
                    <span className="kyc-info-label">تاریخ بررسی</span>
                    <span className="kyc-info-value">{formatDate(row.reviewedAt)}</span>
                  </div>
                )}
                {row.reviewedByUser?.name && (
                  <div className="kyc-info-item">
                    <span className="kyc-info-label">بررسی‌کننده</span>
                    <span className="kyc-info-value">{row.reviewedByUser.name}</span>
                  </div>
                )}
                {row.rejectReason && (
                  <div className="kyc-info-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="kyc-info-label">دلیل رد</span>
                    <span className="kyc-info-value error">{row.rejectReason}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Images */}
            <div className="kyc-images-section">
              <h3 className="kyc-images-title">تصاویر مدارک</h3>
              <div className="kyc-images-grid">
                {renderImage('روی کارت ملی', row.frontImageBase64)}
                {renderImage('پشت کارت ملی', row.backImageBase64)}
                {renderImage('تصویر سلفی', row.selfieImageBase64)}
              </div>
            </div>

            {/* Shift eligibility banner */}
            {pending && <MyShiftStatusBanner category="kyc" />}
          </div>
        )}
      </ResponsiveModal>

      {/* Image lightbox (above the modal) */}
      {previewSrc && (
        <div className="image-preview-overlay" onClick={closePreview}>
          <div className="image-preview-header" onClick={(e) => e.stopPropagation()}>
            <button className="preview-control-btn" onClick={handleZoomOut} title="کوچک‌نمایی">
              <ZoomOutIcon />
            </button>
            <button className="preview-control-btn" onClick={handleZoomIn} title="بزرگ‌نمایی">
              <ZoomInIcon />
            </button>
            <button className="preview-control-btn" onClick={closePreview} title="بستن">
              <CloseIcon />
            </button>
          </div>
          <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewSrc}
              alt="پیش‌نمایش"
              style={{ transform: `scale(${zoom})` }}
              onClick={() => setZoom((z) => (z >= 2 ? 1 : z + 0.5))}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default KycDetailModal;
